import { useState } from 'react';
import { HbsModal } from './hbs/Modal';

interface Props {
  open: boolean;
  /** "Turn on checking" — the caller acknowledges the disclosure and proceeds. */
  onEnable: () => void;
  /** "Not now" / Esc / backdrop. The caller decides what decline means (turn the
   *  setting off on the checkup page; just close in Settings). */
  onClose: () => void;
}

/**
 * The one-time disclosure shown before the breach check's first send. Succinct by
 * default — the one-liner states the privacy guarantee — with the k-anonymity
 * detail behind a "How does this work?" expander for the curious. (Spec §6.3.)
 */
export function BreachCheckDisclosureModal({ open, onEnable, onClose }: Props): JSX.Element | null {
  const [showHow, setShowHow] = useState(false);

  return (
    <HbsModal
      id="breach-disclosure"
      open={open}
      onClose={onClose}
      title="Check for exposed passwords?"
      primaryAction={{ label: 'Turn on checking', onClick: onEnable }}
    >
      <p>
        We check your passwords against known data breaches — without ever sending
        a password.
      </p>
      <button
        type="button"
        id="breach-disclosure-how-toggle"
        onClick={() => setShowHow((v) => !v)}
        className="text-[13px] font-semibold text-hbs-accent hover:underline"
      >
        How does this work?
      </button>
      {showHow && (
        <p className="text-[13px] text-hbs-text-mute">
          Your device sends only the first few characters of a scrambled
          fingerprint (a SHA-1 hash) of each password — never the password itself,
          and nothing that identifies you.
        </p>
      )}
      <div className="pt-1">
        <button
          type="button"
          id="breach-disclosure-decline"
          onClick={onClose}
          className="text-[13px] font-semibold text-hbs-text-mute hover:text-hbs-text"
        >
          Not now
        </button>
      </div>
    </HbsModal>
  );
}
